<template>
    <div class="md:grid md:grid-flow-row md:grid-cols-12 md:gap-4">
        <div 
            role="alert" 
            class="md:col-span-full p-4 mb-4 flex flex-col" 
            v-if="errors.length"
            :class="[configs.button,configs.button_text]"
        >
            <p title="dismiss" class="cursor-pointer self-end text-3xl mb-0" @click="() => this.errors = []" v-text="`\u00D7`"></p>
            <ul class="pl-4">
                <li v-for="(error, eid) in errors" :key="`error_${eid}`" v-text="error" class="mb-2 list-disc"></li>
            </ul>
        </div>
        <div
            v-for="(field, field_key) in conditionals"
            :key="field_key"
            class="mb-4"
            :class="[
                field.cssClass,
                (field.layoutGridColumnSpan != undefined && field.layoutGridColumnSpan < 12) ? `md:col-span-${field.layoutGridColumnSpan}` : 'md:col-span-full'
            ]"
        >
            <Services
                v-if="
                    field.type == 'radio' &&
                    field.cssClass.indexOf('choose-service') !== -1
                "
                :field="field"
                :fields="fields"
                @next="goNext"
            />

            <Providers
                v-else-if="
                    field.type == 'radio' &&
                    field.cssClass.indexOf('choose-provider') !== -1
                "
                :field="field"
                :fields="fields"
            />

            <Toggle
                v-else-if="field.type == 'radio' && field.cssClass.indexOf('choose-toggle') !== -1"
                :field="field"
                :fields="fields"
            />

            <VisitReason
                v-else-if="
                    (field.type == 'text' || field.type == 'select') &&
                    field.cssClass.indexOf('visit-reason') !== -1
                "
                :field="field"
            />

            <Review
                v-else-if="
                    field.type == 'text' &&
                    field.cssClass.indexOf('review-fields') !== -1
                "
                :field="field"
            />
            <Schedule
                v-else-if="(field.type == 'textarea' || field.type == 'date') && 
                field.cssClass.indexOf('schedule-dates') !== -1"
                :field="field"
            />

            <div v-else-if="field.type == 'select'">
                <label class="block mb-2 font-medium" :for="`field_${field.id}`">{{field.label}}<span class="text-bold inline-block ml-2" v-if="field.isRequired" title="This field is required" aria-hidden="true">*</span></label>

                <div class="relative cursor-pointer border-black border border-solid overflow-hidden">
                    <select
                        tabindex="0"
                        :id="`field_${field.id}`"
                        :name="field.id"
                        v-model="fields[field.id]"
                        :required="field.isRequired"
                        class="p-4 w-full pr-12 z-0 relative appearance-none border-none"
                    >
                        <option>Select a value</option>
                        <option
                            v-for="(choice, choice_key) in field.choices"
                            :key="choice_key"
                            :value="choice.value"
                            v-text="choice.text"
                        ></option>
                    </select>
                    <span aria-hidden="true" class="absolute top-0 right-0 bottom-0 w-12 h-full p-4 z-10 text-center bg-gray-100 border-l border-black border-solid pointer-events-none"></span>
                </div>
            </div>

            <div v-else-if="field.type == 'textarea'">
                <label class="block mb-2 font-medium" :for="`field_${field.id}`">{{field.label}}<span class="text-bold inline-block ml-2" v-if="field.isRequired" title="This field is required" aria-hidden="true">*</span></label>

                <textarea
                    tabindex="0"
                    :id="`field_${field.id}`"
                    :name="field.id"
                    v-model="fields[field.id]"
                    :required="field.isRequired"
                    class="p-4 border border-solid border-black w-full"
                ></textarea>
            </div>

            <div v-else-if="field.type == 'radio'" role="radiogroup">
                <label role="heading" aria-level="2" class="block mb-2 font-medium" >{{field.label}}<span class="text-bold inline-block ml-2" v-if="field.isRequired" title="This field is required" aria-hidden="true">*</span></label>

                <div
                    v-for="(choice, choice_key) in field.choices"
                    :key="choice_key"
                    class="flex mb-2"
                >
                    <input
                        tabindex="0"
                        :id="`field_${field.id}_${choice_key}`"
                        :type="types[field.type]"
                        :name="field.id"
                        :value="choice.value"
                        v-model="fields[field.id]"
                        :required="field.isRequired"
                        class="flex-none mr-2 w-5 h-5 appearance-none border border-solid border-black rounded-full ring ring-inset ring-white cursor-pointer"
                        :class="choice.value == fields[field.id] ? $store.state.configs.button : ''"
                    />
                    <label
                        :for="`field_${field.id}_${choice_key}`"
                        v-html="choice.text"
                        class="font-medium cursor-pointer"
                    ></label>
                </div>
            </div>

            <div v-else-if="field.type == 'checkbox'">
                <label role="heading" aria-level="2" class="block mb-2 font-medium" >{{field.label}}<span class="text-bold inline-block ml-2" v-if="field.isRequired" title="This field is required" aria-hidden="true">*</span></label>

                <div
                    v-for="(choice, choice_key) in field.choices"
                    :key="choice_key"
                    class="flex mb-2 cursor-pointer"
                >
                    <input
                        tabindex="0"
                        :id="`field_${field.id}_${choice_key}`"
                        :type="types[field.type]"
                        :name="`${field.id}[${choice_key}]`"
                        :value="choice.value"
                        v-model="fields[field.id][choice_key]"
                        :required="field.isRequired"
                        class="flex-none mr-4 w-5 h-5 appearance-none border border-solid border-black rounded-full ring ring-inset ring-white cursor-pointer"
                        :class="choice.value == fields[field.id] ? $store.state.configs.button : ''"
                        :true-value="choice.value"
                        :false-value="null"
                    />
                    <label
                        :for="`field_${field.id}_${choice_key}`"
                        v-html="choice.text"
                        class="font-medium"
                    ></label>
                </div>
            </div>

            <div v-else-if="field.type == 'html'" v-html="field.content" :data-type="field.type"></div>

            <div v-else-if="field.type == 'number'">
                <label class="block mb-2 font-medium" :for="`field_${field.id}`">{{field.label}}<span class="text-bold inline-block ml-2" v-if="field.isRequired" title="This field is required" aria-hidden="true">*</span></label>
                <input
                    tabindex="0"
                    :type="types[field.type]"
                    :id="`field_${field.id}`"
                    :name="field.id"
                    v-model="fields[field.id]"
                    :required="field.isRequired"
                    class="p-4 border border-solid border-black w-full"
                    :min="field.rangeMin"
                    :max="field.rangeMax"
                    step="0.01"
                />
            </div>

            <div v-else>
                <label class="block mb-2 font-medium" :for="`field_${field.id}`">{{field.label}}<span class="text-bold inline-block ml-2" v-if="field.isRequired" title="This field is required" aria-hidden="true">*</span></label>
                <input
                    tabindex="0"
                    :type="types[field.type]"
                    :id="`field_${field.id}`"
                    :name="field.id"
                    v-model="fields[field.id]"
                    :required="field.isRequired"
                    class="p-4 border border-solid border-black w-full"
                />
            </div>
        </div>
        <div class="flex justify-between mt-4 md:col-span-full" v-if="configs.routing">
            <router-link
                v-if="page && $store.state.step != 1 && $store.state.step != steps.length && page.previousButton.text"
                :to="'/step/' + (+$store.state.step - 1)"
                :class="[configs.button,configs.button_text]" 
                class="justify-self-start block rounded rounded leading-tight text-xl py-4 px-8 mr-4 shadow-lg font-semibold"
                v-text="
                    page.previousButton.text
                "
            ></router-link>
            <router-link
                v-if="page && $store.state.step != steps.length && page.nextButton.text"
                :to="'/step/' + (+$store.state.step + 1)"
                :class="[configs.button,configs.button_text]" 
                class="block rounded rounded leading-tight text-xl py-4 px-8 shadow-lg font-semibold"
                v-text="page.nextButton.text"
                @click.native="e =>
                    submit(e)
                "
            ></router-link>
        </div>
        <div class="flex justify-between mt-4 md:col-span-full" v-else>
            <a
                v-if="page && $store.state.step != 1 && $store.state.step != steps.length && page.previousButton.text"
                href="#"
                :class="[configs.button,configs.button_text]" 
                class="justify-self-start block rounded rounded leading-tight text-xl py-4 px-8 mr-4 shadow-lg font-semibold"
                v-text="page.previousButton.text"
                @click.prevent="
                    $store.commit('updateField', {
                        field : 'step',
                        value : $store.state.step - 1
                    });
                "
            ></a>
            <a
                v-if="page && $store.state.step != steps.length && page.nextButton.text"
                href="#"
                :class="[configs.button,configs.button_text]" 
                class="block rounded rounded leading-tight text-xl py-4 px-8 shadow-lg font-semibold"
                v-text="page.nextButton.text"
                @click.prevent="e => {
                    $store.commit('updateField', {
                        field : 'step',
                        value : $store.state.step + 1
                    });
                    return submit(e)
                }  
                "
            ></a>
        </div>
    </div>
</template>

<script>
import Services from "./fields/Services";
import Toggle from "./fields/Toggle";
import VisitReason from "./fields/VisitReason";
import Providers from "./fields/Providers";
import Schedule from "./fields/Schedule";
import Review from "./fields/Review";
import axios from "axios";

export default {
    data() {
        return {
            fields: {},
            types: {
                'date' : 'date',
                'text' : 'text',
                'phone' : 'tel',
                'email' : 'email',
                'checkbox' : 'checkbox',
                'radio' : 'radio',
                'number' : 'number'
            },
            errors: []
        }
    },
    created() {
        if (!Object.keys(this.steps).length) {
            let steps = {},
                current_page = 1;
            const urlParams = new URLSearchParams(window.location.search);

            for (let i = 0; i < this.form.fields.length; i++) {
                if (typeof steps[current_page] == "undefined") {
                    steps[current_page] = {
                        title: null,
                        page: null,
                        fields: [],
                    };
                }

                if (this.form.fields[i].type == "page") {
                    steps[current_page].page = this.form.fields[i];
                    steps[current_page].title = this.form.pagination.pages[current_page-1]
                    current_page++;
                    continue;
                }
                steps[current_page].fields.push(this.form.fields[i]);

                let data = {};

                // pre-selected values
                switch(this.form.fields[i].type) {
                    case 'checkbox' :
                        data[this.form.fields[i].id] = [];
                        this.form.fields[i].choices.map((choice, key) => {
                            data[this.form.fields[i].id][key] = choice.isSelected ? choice.value : null;
                        });     
                        break;
                    case 'select' :
                    case 'radio' :
                        let $default = this.form.fields[i].choices.find(choice => choice.isSelected);
                        data[this.form.fields[i].id] = $default ? $default.value : this.form.fields[i].defaultValue;
                        break;
                    default :
                        data[this.form.fields[i].id] = this.form.fields[i].defaultValue;
                        break;
                }

                // check for URL params
                if(this.form.fields[i].allowsPrepopulate && urlParams.has(this.form.fields[i].inputName)) {
                    data[this.form.fields[i].id] = urlParams.get(this.form.fields[i].inputName);
                }

                // update the global object
                this.$store.commit("updateData", {
                    field : this.form.fields[i].id,
                    value : data[this.form.fields[i].id]
                });
                
            }
            this.fields = this.$store.state.data;

            this.$store.commit("updateField", {
                field: "steps",
                value: steps,
            });
        }

        if (this.$route.params.step) {
            this.$store.commit("updateField", {
                field: "step",
                value: this.$route.params.step,
            });
        } else {
            this.$store.commit("updateField", {
                field: "step",
                value: 1,
            });
        }

        this.fields = this.$store.state.data;

        this.setFocus();
    },
    methods: {
        setFocus() {
            this.$nextTick(() => {
                let focus = this.$el.querySelector('[tabindex="0"]');
                if(focus.focus != undefined) focus.focus({preventScroll: true});
            });
        },
        goNext() {
            if(this.configs.routing) {
                this.$router.push({ name: "step", params: {step: parseInt(this.step)+1}});
            } else {
                this.$router.replace({ name: "step", params: {step: parseInt(this.step)+1}});
            }
        },
        validate() {
            this.errors = [];
            for(let field of this.conditionals) {
                if(field.isRequired && (this.fields[field.id] == undefined || this.fields[field.id] == "")) {
                    this.errors.push(`${field.label} is required.`);
                }
            }
            return !this.errors.length;
        },
        submit(event) {
            if (event.target.text == "Submit") {
                let obj = {};

                Object.keys(this.fields)
                .map(
                    key => obj[key] = this.handleField(
                                this.getFieldById(key),
                                this.fields[key]
                    )
                );

                axios
                    .post(
                        "/wp-json/gf/v2/entries",
                        {
                            form_id: this.form.id,
                            ...obj,
                        },
                        {
                            auth: {
                                username:
                                    "ck_606c9c75571cdd1dcd91b8d490f685d6bb3c5691",
                                password:
                                    "cs_d01e9524c7c7a08c000b8c81d085c0b8ca0facd2",
                            },
                        }
                    )
                    .then((e) => {

                        if(e.data) {
                            this.$store.commit('updateData', {
                                field: 'submission_id',
                                value: e.data.id,
                            });
                            axios
                                .post(
                                    "/wp-json/gf/v2/entries/" + e.data.id + "/notifications/",
                                    {},
                                    {
                                        auth: {
                                            username:
                                                "ck_606c9c75571cdd1dcd91b8d490f685d6bb3c5691",
                                            password:
                                                "cs_d01e9524c7c7a08c000b8c81d085c0b8ca0facd2",
                                        },
                                    }
                                )
                                .then((e) => {
                                    if(
                                        typeof this.form.personalData.retention != "undefined" &&
                                        this.form.personalData.retention.policy == "delete" && 
                                        this.form.personalData.retention.retain_entries_days == 1
                                    ) {
                                        axios
                                            .delete(
                                                "/wp-json/gf/v2/entries/" + this.fields.submission_id + '?force=1',
                                                {},
                                                {
                                                    auth: {
                                                        username:
                                                            "ck_606c9c75571cdd1dcd91b8d490f685d6bb3c5691",
                                                        password:
                                                            "cs_d01e9524c7c7a08c000b8c81d085c0b8ca0facd2",
                                                    },
                                                }
                                            )
                                            .then((e) => {
                                            });
                                    }
                                });
                        }
                        
                    });
            }
        },
        handleField(name, value) {
            if (name == "provider") {
                if (typeof this.fields.provider == "undefined")
                    return "None";

                return window.departments[
                    this.fields.service
                ].staff.find((e) => e.ID == this.fields.provider)
                    .post_title;
            } else if (name == "days") {
                let text = "";
                for (let u = 0; u < value.length; u++) {
                    text +=
                        this.getDayOfTheWeek(
                            value[u].date
                        ) +
                        " " +
                        this.getDateString(
                            value[u].date
                        ) +
                        "\n" +
                        (value[u].time == null
                            ? "No time preference"
                            : value[u].time) +
                        "\n------------------\n";
                }

                return text;
            } else if (
                name == "reason" &&
                typeof value.post_title != "undefined"
            ) {
                return value.post_title;
            } else {
                return value;
            }
        },
        getFieldById(id) {
            let result = Object
                        .values(this.steps)
                        .flatMap(step => step.fields)
                        .flat()
                        .find(field => field.id == id);
            return result != undefined ? result.inputName : id;
        },
        getDateString(day) {
            let d = new Date(day.replace(/-/g, "/").replace(/T.+/, ""));

            return (
                d.toLocaleString("default", { month: "long" }) +
                " " +
                d.getDate() +
                ", " +
                d.getFullYear()
            );
        },
        getDayOfTheWeek(day) {
            let d = new Date(day.replace(/-/g, "/").replace(/T.+/, ""));

            return this.$store.state.dayOfWeek[d.getDay()];
        },
        passesConditionals(field) {
            // the field is set to hide no matter what
            if(field.visibility != "visible") return false;

            // there are no conditionals, show show
            if (field.conditionalLogic == "") return true;

            // there are no conditional rules, so show
            if (!field.conditionalLogic.rules.length) return true;

            // evaulate the conditionals
            let passes = true,
            results = field.conditionalLogic.rules.map(rule => {
                let input = [], id = rule.fieldId;
                switch(typeof this.fields[id]) {
                    case 'undefined' :
                        // fail if the stored value of this rule doesn't exist...
                        return false;
                    case 'array' :
                    case 'object' :
                        input = this.fields[id];
                        break;
                    default :
                        // set to array for evaluating
                        input.push(this.fields[id]);
                        break;
                }

                // check the rule operators and determine if fields pass/fail...
                switch(rule.operator) {
                    case 'is' :
                        return input.some(option => option == rule.value);
                    case 'isnot' :
                        return !input.some(option => option == rule.value);
                    case '>' :
                        return input.some(option => option > rule.value) && !input.some(option => option < rule.value);
                    case '<' :
                        return input.some(option => option < rule.value) && !input.some(option => option > rule.value);
                    case 'contains' :
                        return input.some(option => option !== null && option.indexOf(rule.value) != -1);
                    case 'starts_with' :
                        return input.some(option => option !== null && option.startsWith(rule.value));
                    case 'ends_with' :
                        return input.some(option => option !== null && option.endsWith(rule.value));
                    default:
                        // no other documented criteria  exist, so we must assume true
                        return true;
                }
                
            });

            switch(field.conditionalLogic.logicType) {
                case 'any' :
                    // if any true exists in results, it's true
                    passes = results.some(result => result === true);
                    break;
                case 'all' :
                    // if any false exists in results, it's false
                    passes = !results.some(result => result === false);
                    break;
            }

            switch(field.conditionalLogic.actionType) {
                case 'show' :
                    // true results in field showing, false hides
                    return passes;
                case 'hide' :
                    // false results in field showing, true hides
                    return !passes;
            }
        },        
    },
    computed: {
        conditionals() {
            let fields = this.steps[this.step].fields;
            return fields.filter(field => {
                let passes = this.passesConditionals(field);
                return passes;
            })
        },
        form() {
            return window.booking_form;
        },
        steps() {
            return this.$store.state.steps;
        },
        step() {
            return this.$store.state.step;
        },
        page() {
            return this.$store.state.steps[this.step].page;
        },
        configs() {
            return this.$store.state.configs;
        }
    },
    components: {
        Services,
        Toggle,
        VisitReason,
        Providers,
        Schedule,
        Review,
    },
    watch: {
        fields: {
            deep: true,
            lazy: false,
            handler(p) {
                this.$store.commit("updateField", {
                    field: "data",
                    value: p
                });
            }
        }
    },
    beforeRouteEnter (to, from, next) {
        if(from.path == "/" && to.name != "home") {
            next({name: "home"});
        }
        next();
    },
    beforeRouteLeave (to, from, next) {
        if (this.validate()) {
            next()
        } else {
            next(false)
        }
    },
    beforeRouteUpdate (to, from, next) {
        if(to.params.step < from.params.step) {
            next();
        }
        if (this.validate()) {
            next()
        } else {
            next(false)
        }
    }
};
</script>

<style lang="scss">
#vue-app {
    select {
        ~ span {
            &::after {
                content: "\25BC";
            }
        }
    }
}
</style>